import React, { useState } from 'react';
import styles from './ReviewModal.module.css';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

interface ReviewModalProps {
  productId: string;
  onClose: () => void;
  onReviewSubmitted: () => void;
}

const ReviewModal: React.FC<ReviewModalProps> = ({ productId, onClose, onReviewSubmitted }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      setImage(selectedFile);
      
      // Kreiranje pregleda slike
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target?.result as string);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name || !email || !reviewText) {
      toast.error('Molimo popunite sva obavezna polja.');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      let imageUrl = null;
      
      // Upload slike ako je ona izabrana
      if (image) {
        const fileExt = image.name.split('.').pop();
        const fileName = `${Date.now()}.${fileExt}`;
        const filePath = `reviews/${fileName}`;
        
        const { error: uploadError, data } = await supabase.storage
          .from('reviews')
          .upload(filePath, image);
          
        if (uploadError) {
          throw uploadError;
        }
        
        // Dobijanje javnog URL-a slike
        const { data: { publicUrl } } = supabase.storage
          .from('reviews')
          .getPublicUrl(filePath);
          
        imageUrl = publicUrl;
      }
      
      // Kreiranje recenzije u bazi
      const { error } = await supabase
        .from('reviews')
        .insert({
          product_id: productId,
          customer_name: name,
          customer_email: email,
          rating,
          review_text: reviewText,
          image_url: imageUrl,
        });
        
      if (error) throw error;
      
      toast.success('Vaša recenzija je uspešno poslata i biće objavljena nakon pregleda.');
      onReviewSubmitted();
      onClose();
    } catch (error) {
      console.error('Greška prilikom slanja recenzije:', error);
      toast.error('Došlo je do greške. Molimo pokušajte ponovo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <button className={styles.closeButton} onClick={onClose}>×</button>
        <h2>Napišite recenziju</h2>
        
        <form onSubmit={handleSubmit} className={styles.reviewForm}>
          <div className={styles.formGroup}>
            <label htmlFor="name">Ime *</label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          
          <div className={styles.formGroup}>
            <label htmlFor="email">E-mail *</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          
          <div className={styles.formGroup}>
            <label>Ocena *</label>
            <div className={styles.ratingSelector}>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className={styles.starButton}
                  onClick={() => setRating(star)}
                >
                  <span className={star <= rating ? styles.starFilled : styles.starEmpty}>★</span>
                </button>
              ))}
            </div>
          </div>
          
          <div className={styles.formGroup}>
            <label htmlFor="review">Recenzija *</label>
            <textarea
              id="review"
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              rows={5}
              required
            />
          </div>
          
          <div className={styles.formGroup}>
            <label htmlFor="image">Dodajte sliku (opciono)</label>
            <input
              type="file"
              id="image"
              onChange={handleImageChange}
              accept="image/*"
            />
            {imagePreview && (
              <div className={styles.imagePreview}>
                <img src={imagePreview} alt="Preview" />
              </div>
            )}
          </div>
          
          <button
            type="submit"
            className={styles.submitButton}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Slanje...' : 'Pošalji recenziju'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ReviewModal; 